import { useMemo } from 'react';
import { faDownload } from '@fortawesome/pro-regular-svg-icons';

import { AnyObject, JSONSchema } from '@typings';
import { cn, getParamsSchema, isString } from '@utils';

import {
  Button,
  ChaseSpinner,
  Icon,
  Render,
  Tooltip,
} from '@components/Shared';

import { ParamsSchema } from './ParamsSchema';

type Props = {
  title: string;
  params: AnyObject | string | null;
  schema?: JSONSchema;
  isLoading?: boolean;
  onDownload?: () => void;
};

export const SchemaParamsSection: React.FC<Props> = ({
  title,
  params,
  schema,
  isLoading,
  onDownload,
}) => {
  const paramsSchema = useMemo(
    () => (schema ? getParamsSchema(schema) : null),
    [schema],
  );

  const renderContent = () => {
    if (!params || isLoading) {
      return (
        <div className="my-6 flex justify-center">
          <ChaseSpinner color="black" className="h-10 w-10" />
        </div>
      );
    }

    if (!schema) {
      return <p>App template was not found for this version</p>;
    }

    if (!paramsSchema) {
      return <p>Could not extract app schema</p>;
    }

    if (isString(params)) {
      return <p>{params}</p>;
    }

    return (
      <div className="flex flex-col gap-10 font-roboto">
        <ParamsSchema isTopLevel schema={params} params={paramsSchema} />
      </div>
    );
  };

  return (
    <div className="mt-4 flex flex-col gap-4">
      <h3 className="flex flex-row items-center gap-3 text-h5 capitalize">
        {title}

        <Render if={onDownload}>
          <Tooltip disabled={!params}>
            <div slot="trigger">
              <Button
                theme
                disabled={!params}
                onClick={onDownload}
                aria-label="Download parameters"
                className={cn(
                  'cursor-not-allowed opacity-50 transition-opacity will-change-transform',
                  {
                    'cursor-pointer hover:opacity-100': params,
                  },
                )}
              >
                <Icon icon={faDownload} className="text-[20px]" />
              </Button>
            </div>
            Download parameters
          </Tooltip>
        </Render>
      </h3>
      {renderContent()}
    </div>
  );
};
